/**
 * <pw-name-clicker-builder>: the Build Your Own Name Clicker customer experience.
 *
 * Reads its validated product/variant configuration and the catalogue from the JSON blocks the
 * section embeds and keeps an ordered sequence of modules: one letter module per character of the
 * typed name plus any icon modules the customer inserts (before, between or after letters, as often as
 * they like). Every module is customised independently (body colour, cap colour, cap style for letters when the
 * section offers white caps, and, for icons, the icon itself). A letter cap is a colour base with a white letter or a
 * white base with a colour letter; icons are always white on a colour base. The build is previewed, priced in integer cents from the
 * sequence length and added to the cart as ONE line on the variant of its module count and attachment.
 * All pricing and schema rules come from assets/pw-name-clicker-schema.js; nothing here invents a colour
 * or a design.
 */

import { buildCartItems, findEntry, formatZar, newConfigId } from '@theme/pw-config-core';
import * as schema from '@theme/pw-name-clicker-schema';
import { addBuildToCart } from '@theme/pw-config-cart';
import { announce, assetUrl, createStepFlow, el, readJsonBlock, renderRadioGroup, replaceChildren, setStepDone } from '@theme/pw-config-ui';

class PwNameClickerBuilder extends HTMLElement {
  #config = null;
  #catalogue = null;
  #catalogueProblems = [];
  #modules = [];
  #activeIndex = 0;
  /** Sequence index the add panel inserts at, or null when the panel is closed. */
  #insertAt = null;
  #attachment = schema.DEFAULT_ATTACHMENT;
  /** True once the customer has picked an attachment card (the price default is the keyring; step 3 still asks). */
  #attachmentChosen = false;
  /** Assembly choice id (ASSEMBLY_OPTIONS); null until the customer picks one — Add to cart stays disabled. */
  #assembly = null;
  #busy = false;
  #refs = {};
  /** Progressive steps: name → modules → attachment → assembly (pw-config-ui createStepFlow). */
  #flow = null;

  connectedCallback() {
    this.#config = readJsonBlock(this, 'script[data-pwc-config]', { errors: ['Builder configuration is missing.'] });
    this.#catalogue = readJsonBlock(this, 'script[data-pwc-catalogue]', null);
    this.#catalogueProblems = schema.validateCatalogue(this.#catalogue);
    for (const node of this.querySelectorAll('[data-pwc]')) this.#refs[node.dataset.pwc] = node;
    this.#flow = createStepFlow(
      this,
      [
        { id: 'name', valid: () => this.#modules.length > 0, summary: () => `${this.#sequenceText()} · ${this.#countText()}` },
        { id: 'modules', valid: () => this.#modules.length > 0 && this.#moduleProblems.length === 0, summary: () => `${this.#countText()} customised` },
        { id: 'attachment', valid: () => this.#attachmentChosen, summary: () => this.#attachmentSummary() },
        { id: 'assembly', valid: () => Boolean(schema.ASSEMBLY_OPTIONS[this.#assembly]), summary: () => this.#assemblyLabel },
      ],
      { onEdit: () => this.#afterNavigation() }
    );

    this.#verifyConfig();
    this.#bind();
    this.#bindGallery();
    this.#render();
  }

  /* ---------------------------------------------------------------- product photo gallery */

  /** Thumbnails switch the main photo (all product images are rendered by the section; one is visible at a time). */
  #bindGallery() {
    const gallery = this.querySelector('[data-pwc-gallery]');
    if (!gallery) return;
    const slides = [...gallery.querySelectorAll('[data-pwc-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-pwc-thumb]')];
    const show = (index) => {
      slides.forEach((slide, position) => (slide.hidden = position !== index));
      thumbs.forEach((thumb, position) => thumb.setAttribute('aria-current', position === index ? 'true' : 'false'));
    };
    thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => show(index));
      thumb.addEventListener('keydown', (event) => {
        const delta = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (delta === undefined) return;
        event.preventDefault();
        const next = (index + delta + thumbs.length) % thumbs.length;
        thumbs[next].focus();
        show(next);
      });
    });
  }

  /* ---------------------------------------------------------------- progressive steps */

  /** A step's Continue button (or Enter in the name field): move on when the step is valid, else say why not. */
  #continue(stepId) {
    if (!this.#flow.advance(stepId)) {
      announce(this, stepId === 'name' ? 'Type a name or add an icon first.' : this.#priceBlocker || 'Complete this step first.');
      return;
    }
    this.#afterNavigation();
  }

  /** A card step (attachment, assembly) completes itself on the click; the last step stays open. */
  #completeCardStep(stepId) {
    this.#flow.advance(stepId);
    this.#afterNavigation();
  }

  #afterNavigation() {
    this.#render();
    this.#flow.focusCurrent();
    announce(this, `Step: ${this.querySelector(`[data-pwc-step="${this.#flow.current}"] .pwc-step__heading`)?.textContent || this.#flow.current}.`);
  }

  #countText() {
    const count = this.#modules.length;
    return `${count} ${count === 1 ? 'module' : 'modules'}`;
  }

  #attachmentSummary() {
    const attachment = schema.ATTACHMENTS[this.#attachment];
    return attachment.included ? `${attachment.label} (included)` : `${attachment.label} (+${formatZar(attachment.priceCents)})`;
  }

  /* ---------------------------------------------------------------- configuration integrity */

  get #configErrors() {
    return Array.isArray(this.#config?.errors) ? this.#config.errors : [];
  }

  /** Defence in depth: the section validated prices in Liquid; re-check here against the schema. */
  #verifyConfig() {
    const errors = this.#configErrors;
    if (!errors.length) errors.push(...schema.matrixProblems(this.#config?.variants).slice(0, 3));
    if (this.#catalogueProblems.length) errors.push(`Catalogue is invalid: ${this.#catalogueProblems[0]}`);
    this.#config = { ...(this.#config || {}), errors };
  }

  /* ------------------------------------------------------------------------------- events */

  #bind() {
    const { 'name-input': input, form, attachment, assembly } = this.#refs;
    input?.addEventListener('input', () => this.#onNameInput());
    input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.#continue('name');
    });
    for (const button of this.querySelectorAll('[data-pwc-continue]')) {
      button.addEventListener('click', () => this.#continue(button.dataset.pwcContinue));
    }
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#addToCart();
    });
    // Card steps complete on the pick: `change` for a new value, `click` so re-picking the same card after an Edit also moves on.
    const onAttachment = (event) => {
      const value = event.target?.value;
      if (!event.target?.matches?.('input[type="radio"]') || !schema.ATTACHMENTS[value]) return;
      this.#attachment = value;
      this.#attachmentChosen = true;
      setStepDone(this, 'attachment', true);
      this.#completeCardStep('attachment');
    };
    attachment?.addEventListener('change', onAttachment);
    attachment?.addEventListener('click', onAttachment);
    const onAssembly = (event) => {
      const value = event.target?.value;
      if (!event.target?.matches?.('input[type="radio"]') || !schema.ASSEMBLY_OPTIONS[value]) return;
      this.#assembly = value;
      setStepDone(this, 'assembly', true);
      this.#completeCardStep('assembly');
    };
    assembly?.addEventListener('change', onAssembly);
    assembly?.addEventListener('click', onAssembly);
    this.#refs['move-left']?.addEventListener('click', () => this.#move(-1));
    this.#refs['move-right']?.addEventListener('click', () => this.#move(1));
    this.#refs['remove-module']?.addEventListener('click', () => this.#removeActive());
    this.#refs['letter-input']?.addEventListener('input', () => this.#onLetterInput());
    this.#refs['add-letter-form']?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.#addLetterFromPanel();
    });
    this.#refs['add-letter-input']?.addEventListener('input', (event) => {
      const { name } = schema.normaliseName(event.target.value);
      event.target.value = name.slice(-1);
    });
    this.#refs['add-cancel']?.addEventListener('click', () => this.#closeAddPanel());
  }

  #onNameInput() {
    const input = this.#refs['name-input'];
    const { name, dropped } = schema.normaliseName(input.value);
    const caret = input.selectionStart;
    if (input.value !== name) {
      input.value = name;
      try {
        input.setSelectionRange(caret, caret);
      } catch (_) {}
    }
    this.#refs['name-hint'].textContent = dropped ? 'Letters A to Z only. Add icons with the + buttons below.' : '';
    this.#refs['name-hint'].classList.toggle('is-error', dropped);
    this.#modules = schema.reconcileModules(this.#modules, name, this.#catalogue);
    this.#activeIndex = Math.min(this.#activeIndex, Math.max(0, this.#modules.length - 1));
    this.#insertAt = null;
    this.#render();
  }

  /** Changing the letter of the active letter module in place (its colours stay). */
  #onLetterInput() {
    const input = this.#refs['letter-input'];
    const module = this.#modules[this.#activeIndex];
    const { name } = schema.normaliseName(input.value);
    const character = name.slice(-1);
    input.value = character || (module && schema.isLetterModule(module) ? module.character : '');
    if (!character || !module || !schema.isLetterModule(module) || module.character === character) return;
    this.#modules = this.#modules.map((entry, index) => (index === this.#activeIndex ? { ...entry, character } : entry));
    this.#syncNameInput();
    announce(this, `Module ${this.#activeIndex + 1} is now the letter ${character}.`);
    this.#render();
  }

  #selectModule(index) {
    this.#activeIndex = index;
    this.#insertAt = null;
    this.#render();
    this.#refs['module-editor']?.focus({ preventScroll: false });
  }

  #move(delta) {
    const to = this.#activeIndex + delta;
    if (to < 0 || to >= this.#modules.length) return;
    this.#modules = schema.moveModule(this.#modules, this.#activeIndex, to);
    this.#activeIndex = to;
    this.#syncNameInput();
    announce(this, `Module moved to position ${to + 1}. The sequence is now ${this.#sequenceText()}.`);
    this.#render();
  }

  #removeActive() {
    if (!this.#modules.length) return;
    const removed = this.#modules[this.#activeIndex];
    this.#modules = schema.removeModule(this.#modules, this.#activeIndex);
    this.#activeIndex = Math.max(0, Math.min(this.#activeIndex, this.#modules.length - 1));
    this.#syncNameInput();
    announce(this, `Removed ${this.#moduleName(removed)}. ${this.#modules.length ? `The sequence is now ${this.#sequenceText()}.` : 'No modules left.'}`);
    this.#render();
    this.#refs['name-input']?.focus();
  }

  #syncNameInput() {
    const input = this.#refs['name-input'];
    if (input) input.value = schema.nameFromModules(this.#modules);
  }

  /* ------------------------------------------------------------------------- insertion */

  #openAddPanel(index) {
    this.#insertAt = Math.max(0, Math.min(index, this.#modules.length));
    this.#render();
    const letter = this.#refs['add-letter-input'];
    if (letter) {
      letter.value = '';
      letter.focus({ preventScroll: false });
    }
  }

  #closeAddPanel() {
    this.#insertAt = null;
    this.#render();
  }

  #insert(module) {
    const at = this.#insertAt ?? this.#modules.length;
    this.#modules = schema.insertModule(this.#modules, at, module);
    this.#activeIndex = at;
    this.#insertAt = null;
    this.#syncNameInput();
    announce(this, `Added ${this.#moduleName(module)} at position ${at + 1}. The sequence is now ${this.#sequenceText()}.`);
    this.#render();
    this.#refs['module-editor']?.focus({ preventScroll: false });
  }

  #addLetterFromPanel() {
    const input = this.#refs['add-letter-input'];
    const { name } = schema.normaliseName(input?.value);
    const character = name.slice(-1);
    if (!character) {
      input?.focus();
      return;
    }
    const at = this.#insertAt ?? this.#modules.length;
    this.#insert(schema.createModule(character, this.#catalogue, this.#modules[at - 1]));
  }

  #addIcon(iconId) {
    const at = this.#insertAt ?? this.#modules.length;
    this.#insert(schema.createIconModule(iconId, this.#catalogue, this.#modules[at - 1]));
  }

  /* ------------------------------------------------------------------------------ options */

  /**
   * Sets one selection on the active module or, with `applyToAll`, on every module it can apply to:
   * body colour and cap base on all modules, an icon only on icon modules. Applying to all never changes
   * a module's type, letter, icon-or-letter nature or position.
   */
  #setOption(fieldKey, entryId, applyToAll = false) {
    let applied = 0;
    this.#modules = this.#modules.map((module, index) => {
      if (!applyToAll && index !== this.#activeIndex) return module;
      if (fieldKey === 'capDesignId' && schema.isLetterModule(module)) return module; // a letter's cap design is the letter itself
      applied += 1;
      return { ...module, [fieldKey]: entryId };
    });
    if (applyToAll) announce(this, `Applied to all ${applied} ${fieldKey === 'capDesignId' ? 'icon modules' : 'modules'}.`);
    this.#render();
  }

  /* ----------------------------------------------------------------------------- derived */

  get #moduleProblems() {
    const problems = [];
    this.#modules.forEach((module, index) => {
      for (const field of schema.MODULE_FIELDS) {
        const label = (schema.isIconModule(module) && schema.ICON_FIELD_LABELS[field.key]) || field.label;
        // The cap colour is the base of a coloured cap or the lettering of a white cap; both come from capBaseColours.
        const value = field.key === 'capBaseColourId' ? schema.capColourId(module) : module[field.key];
        if (!findEntry(this.#catalogue?.[field.group], value)) problems.push({ index, label: field.key === 'capBaseColourId' ? 'cap colour' : label });
      }
    });
    return problems;
  }

  /** True when the store offers white caps with coloured lettering (section setting). */
  get #whiteCapsOffered() {
    return this.#config?.whiteCaps === true;
  }

  /** Swatch of a cap colour id, white base included. */
  #capSwatch(id) {
    return id === schema.WHITE_CAP_BASE.id ? schema.WHITE_CAP_BASE.swatch : this.#entry('capBaseColours', id)?.swatch;
  }

  /**
   * Sets the cap style ("coloured" | "white") of the active letter module or, with `applyToAll`, of every letter module.
   * Each module keeps its own cap colour; icons are never changed (their caps are always coloured).
   */
  #setCapStyle(style, applyToAll = false) {
    let applied = 0;
    this.#modules = this.#modules.map((module, index) => {
      if ((!applyToAll && index !== this.#activeIndex) || !schema.isLetterModule(module)) return module;
      applied += 1;
      return schema.withCap(module, style, schema.capColourId(module));
    });
    if (applyToAll) announce(this, `Applied to all ${applied} letter ${applied === 1 ? 'module' : 'modules'}.`);
    this.#render();
  }

  /** Sets the cap colour of the active module or, with `applyToAll`, of every module; each keeps its cap style. */
  #setCapColour(colourId, applyToAll = false) {
    let applied = 0;
    this.#modules = this.#modules.map((module, index) => {
      if (!applyToAll && index !== this.#activeIndex) return module;
      applied += 1;
      return schema.withCap(module, schema.capStyle(module), colourId);
    });
    if (applyToAll) announce(this, `Applied to all ${applied} modules.`);
    this.#render();
  }

  /** Why the build cannot be priced yet (store setup, catalogue, modules), or '' when the total is live. */
  get #priceBlocker() {
    if (this.#configErrors.length) return 'This builder is not configured yet. Please check back soon.';
    if (!schema.catalogueIsComplete(this.#catalogue)) return 'Colour and design options are coming soon.';
    if (!this.#modules.length) return 'Type a name to start your build.';
    if (this.#modules.length > schema.MAX_MODULES) return `A Name Clicker can have up to ${schema.MAX_MODULES} modules. Contact us for a longer one.`;
    const problem = this.#moduleProblems[0];
    if (problem) return `Choose a ${problem.label.toLowerCase()} for module ${problem.index + 1}.`;
    return '';
  }

  /** Why Add to cart is disabled: a pricing problem, else the attachment card, else the assembly choice still to make. */
  get #blocker() {
    if (this.#priceBlocker) return this.#priceBlocker;
    if (!this.#attachmentChosen) return 'Choose an attachment in step 3.';
    return schema.ASSEMBLY_OPTIONS[this.#assembly] ? '' : 'Choose Self assembly or Assemble for me.';
  }

  get #assemblyLabel() {
    return schema.ASSEMBLY_OPTIONS[this.#assembly]?.label || '';
  }

  get #pricing() {
    return schema.priceConfiguration(this.#modules.length, this.#attachment);
  }

  #entry(group, id) {
    return findEntry(this.#catalogue?.[group], id);
  }

  #moduleName(module) {
    if (!module) return 'module';
    return schema.isIconModule(module) ? `the ${this.#entry('capDesigns', module.capDesignId)?.label || module.capDesignId} icon` : `the letter ${module.character}`;
  }

  #sequenceText() {
    return schema.sequenceLabel(this.#modules, this.#catalogue);
  }

  /* ---------------------------------------------------------------------------- rendering */

  #render() {
    this.#renderErrors();
    this.#renderPreview();
    this.#renderModules();
    this.#renderAddPanel();
    this.#renderEditor();
    this.#renderSummary();
    setStepDone(this, 'name', this.#modules.length > 0);
    setStepDone(this, 'modules', this.#modules.length > 0 && this.#moduleProblems.length === 0);
    const continueName = this.querySelector('[data-pwc-continue="name"]');
    if (continueName) continueName.disabled = this.#modules.length === 0;
    const continueModules = this.querySelector('[data-pwc-continue="modules"]');
    if (continueModules) continueModules.disabled = !(this.#modules.length > 0 && this.#moduleProblems.length === 0);
    this.#flow?.render();
  }

  #renderErrors() {
    const box = this.#refs['config-error'];
    if (!box) return;
    const errors = this.#configErrors;
    box.hidden = errors.length === 0;
    replaceChildren(
      box,
      el('p', { className: 'pwc-alert__title', text: 'This builder is not available yet.' }),
      el('p', { className: 'pwc-alert__text', text: 'Add to cart is disabled until the store setup is complete.' }),
      errors.length
        ? el('ul', { className: 'pwc-alert__list' }, errors.map((error) => el('li', { text: error })))
        : null
    );
    const empty = this.#refs['catalogue-empty'];
    if (empty) empty.hidden = errors.length > 0 || schema.catalogueIsComplete(this.#catalogue);
  }

  /** The face of a module: its letter, or its icon image (label as fallback). */
  #moduleFace(module, className) {
    if (schema.isLetterModule(module)) return el('span', { className: `${className}__char`, text: module.character });
    const design = this.#entry('capDesigns', module.capDesignId);
    const src = assetUrl(this.#config?.assetBase, design?.image);
    if (src) return el('img', { className: `${className}__icon`, src, alt: '', width: '32', height: '32', loading: 'lazy' });
    return el('span', { className: `${className}__char ${className}__char--icon`, text: design?.label || module.capDesignId });
  }

  #renderPreview() {
    const list = this.#refs.preview;
    if (!list) return;
    if (!this.#modules.length) {
      replaceChildren(list, el('li', { className: 'pwc-preview__empty', text: 'Your modules will appear here in order: one clicker per letter or icon.' }));
    } else {
      replaceChildren(
        list,
        ...this.#modules.map((module, index) => {
          const body = this.#entry('bodyColours', module.bodyColourId);
          const capSwatch = this.#capSwatch(module.capBaseColourId);
          const charSwatch = module.characterColourId === schema.LETTER_COLOUR.id ? schema.LETTER_COLOUR.swatch : this.#capSwatch(module.characterColourId);
          const design = this.#entry('capDesigns', module.capDesignId);
          const style = [
            body?.swatch ? `--pwc-body:${body.swatch}` : '',
            capSwatch ? `--pwc-cap:${capSwatch}` : '',
            charSwatch ? `--pwc-char:${charSwatch}` : '',
          ]
            .filter(Boolean)
            .join(';');
          const title = schema.isIconModule(module) ? `${design?.label || module.capDesignId} icon` : design ? `${module.character}: ${design.label}` : module.character;
          return el(
            'li',
            {
              className: `pwc-module-tile${index === this.#activeIndex ? ' is-active' : ''}${schema.isIconModule(module) ? ' pwc-module-tile--icon' : ''}`,
              style,
              title,
            },
            [el('span', { className: 'pwc-module-tile__cap' }, [this.#moduleFace(module, 'pwc-module-tile')])]
          );
        })
      );
    }
    const attachment = this.#refs['preview-attachment'];
    if (attachment) attachment.textContent = this.#modules.length && this.#attachmentChosen ? schema.ATTACHMENTS[this.#attachment].label : '';
  }

  /** The chip strip: an insert button before every module and one after the last (the only control when empty). */
  #renderModules() {
    const list = this.#refs.modules;
    if (!list) return;
    const problems = this.#moduleProblems;
    const total = this.#modules.length;
    const insertButton = (index) => {
      const atEnd = index === total;
      const label = total === 0 ? 'Add a module' : atEnd ? 'Add a module at the end' : `Insert a module before module ${index + 1}`;
      return el('li', { className: `pwc-chip-item pwc-chip-item--insert${atEnd ? ' pwc-chip-item--end' : ''}` }, [
        el(
          'button',
          {
            type: 'button',
            className: `pwc-insert${atEnd ? ' pwc-insert--end' : ''}${this.#insertAt === index ? ' is-active' : ''}`,
            'aria-label': label,
            'aria-expanded': this.#insertAt === index ? 'true' : 'false',
            'aria-controls': 'pwc-nc-add-panel',
            title: label,
            dataset: { insertAt: String(index) },
            onClick: () => (this.#insertAt === index ? this.#closeAddPanel() : this.#openAddPanel(index)),
          },
          [el('span', { 'aria-hidden': 'true', text: '+' }), atEnd ? el('span', { className: 'pwc-insert__text', text: total === 0 ? 'Add a letter or icon' : 'Add' }) : null]
        ),
      ]);
    };
    const items = [];
    this.#modules.forEach((module, index) => {
      const incomplete = problems.some((problem) => problem.index === index);
      const icon = schema.isIconModule(module);
      items.push(insertButton(index));
      items.push(
        el('li', { className: 'pwc-chip-item' }, [
          el(
            'button',
            {
              type: 'button',
              className: `pwc-chip${index === this.#activeIndex ? ' is-active' : ''}${incomplete ? ' is-incomplete' : ''}${icon ? ' pwc-chip--icon' : ''}`,
              'aria-current': index === this.#activeIndex ? 'true' : undefined,
              'aria-label': `Module ${index + 1}, ${icon ? `${this.#entry('capDesigns', module.capDesignId)?.label || module.capDesignId} icon` : module.character}${incomplete ? ', needs options' : ''}`,
              onClick: () => this.#selectModule(index),
            },
            [el('span', { className: 'pwc-chip__num', text: String(index + 1) }), this.#moduleFace(module, 'pwc-chip')]
          ),
        ])
      );
    });
    items.push(insertButton(total));
    replaceChildren(list, ...items);
    const hint = this.#refs['modules-hint'];
    if (hint) hint.hidden = total > 0;
  }

  #renderAddPanel() {
    const panel = this.#refs['add-panel'];
    if (!panel) return;
    const open = this.#insertAt !== null;
    panel.hidden = !open;
    if (!open) return;
    const at = this.#insertAt;
    const total = this.#modules.length;
    const where = total === 0 ? 'Start your build' : at === 0 ? 'Add at the start' : at === total ? 'Add at the end' : `Add between module ${at} and module ${at + 1}`;
    const title = this.#refs['add-title'];
    if (title) title.textContent = `${where} (position ${at + 1})`;
    const icons = schema.iconDesigns(this.#catalogue);
    replaceChildren(
      this.#refs['add-icons'],
      renderRadioGroup({
        id: 'pwc-nc-add-icon',
        label: 'Add an icon module',
        entries: icons,
        selectedId: null,
        assetBase: this.#config?.assetBase,
        emptyText: 'Icon options are coming soon.',
        onSelect: (id) => this.#addIcon(id),
      })
    );
  }

  #renderEditor() {
    const editor = this.#refs['module-editor'];
    if (!editor) return;
    const module = this.#modules[this.#activeIndex];
    editor.hidden = !module;
    if (!module) return;
    const total = this.#modules.length;
    const icon = schema.isIconModule(module);
    const iconLabel = icon ? `${this.#entry('capDesigns', module.capDesignId)?.label || module.capDesignId} icon` : '';
    this.#refs['editor-title'].textContent = `Module ${this.#activeIndex + 1} of ${total}: ${icon ? iconLabel : module.character}`;
    this.#refs['move-left'].disabled = this.#activeIndex === 0;
    this.#refs['move-right'].disabled = this.#activeIndex === total - 1;
    const letterField = this.#refs['letter-field'];
    if (letterField) letterField.hidden = icon;
    const letterInput = this.#refs['letter-input'];
    if (letterInput && !icon && letterInput.value !== module.character) letterInput.value = module.character;

    const iconModules = this.#modules.filter(schema.isIconModule).length;
    const letterCount = total - iconModules;
    const whiteCap = schema.capStyle(module) === schema.CAP_STYLES.white.id;
    const capColour = schema.capColourId(module);
    const groups = schema.MODULE_FIELDS.filter((field) => icon || field.key !== 'capDesignId').flatMap((field) => {
      if (field.key === 'capBaseColourId') {
        const groupsForCap = [];
        // Letter caps: coloured base + white letter, or (when offered) white base + coloured letter. A module already
        // in the white style keeps the control so it can always be switched back.
        if (!icon && (this.#whiteCapsOffered || whiteCap)) {
          const sample = this.#capSwatch(capColour) || schema.WHITE_CAP_BASE.swatch;
          groupsForCap.push(
            renderRadioGroup({
              id: 'pwc-nc-capStyle',
              label: 'Cap style',
              entries: [
                { id: schema.CAP_STYLES.coloured.id, label: schema.CAP_STYLES.coloured.label, swatch: sample, text: schema.LETTER_COLOUR.swatch },
                { id: schema.CAP_STYLES.white.id, label: schema.CAP_STYLES.white.label, swatch: schema.WHITE_CAP_BASE.swatch, text: sample },
              ],
              selectedId: schema.capStyle(module),
              onSelect: (id) => this.#setCapStyle(id),
              action: {
                label: 'Use for all letters',
                disabled: letterCount < 2,
                title: letterCount < 2 ? 'Add another letter to use this style for all of them' : undefined,
                onClick: () => this.#setCapStyle(schema.capStyle(module), true),
              },
            })
          );
        }
        const colourLabel = whiteCap ? 'Letter colour' : field.label;
        const entries = (this.#catalogue?.[field.group] || []).map((entry) =>
          whiteCap ? { ...entry, swatch: schema.WHITE_CAP_BASE.swatch, text: entry.swatch } : icon || !entry.swatch ? entry : { ...entry, text: schema.LETTER_COLOUR.swatch }
        );
        groupsForCap.push(
          renderRadioGroup({
            id: 'pwc-nc-capColour',
            label: colourLabel,
            entries,
            selectedId: capColour,
            assetBase: this.#config?.assetBase,
            emptyText: `${colourLabel} options are coming soon.`,
            onSelect: (id) => this.#setCapColour(id),
            action: {
              label: 'Use for all modules',
              disabled: total < 2,
              title: total < 2 ? 'Add another module to use this for all of them' : undefined,
              onClick: () => this.#setCapColour(capColour, true),
            },
          })
        );
        return groupsForCap;
      }
      const isDesign = field.key === 'capDesignId';
      const entries = isDesign ? schema.iconDesigns(this.#catalogue) : this.#catalogue?.[field.group] || [];
      const label = (icon && schema.ICON_FIELD_LABELS[field.key]) || field.label;
      const targets = isDesign ? iconModules : total;
      return renderRadioGroup({
        id: `pwc-nc-${field.key}`,
        label,
        entries,
        selectedId: module[field.key],
        assetBase: this.#config?.assetBase,
        emptyText: `${label} options are coming soon.`,
        onSelect: (id) => this.#setOption(field.key, id),
        action: {
          label: isDesign ? 'Use for all icon modules' : 'Use for all modules',
          disabled: targets < 2,
          title: targets < 2 ? (isDesign ? 'Add another icon module to use this icon for all of them' : 'Add another module to use this for all of them') : undefined,
          onClick: () => this.#setOption(field.key, module[field.key], true),
        },
      });
    });
    replaceChildren(this.#refs.groups, ...groups);
  }

  #renderSummary() {
    const { moduleCents, attachmentCents, totalCents } = this.#pricing;
    const count = this.#modules.length;
    const letters = schema.letterModules(this.#modules).length;
    const icons = count - letters;
    const attachment = schema.ATTACHMENTS[this.#attachment];
    const summary = this.#refs.summary;
    if (summary) {
      const breakdown = count ? (icons ? ` (${letters} ${letters === 1 ? 'letter' : 'letters'}, ${icons} ${icons === 1 ? 'icon' : 'icons'})` : '') : '';
      replaceChildren(
        summary,
        el('div', { className: 'pwc-line' }, [
          el('dt', { text: count ? `${count} ${count === 1 ? 'module' : 'modules'} × ${formatZar(schema.MODULE_PRICE_CENTS)}${breakdown}` : 'Modules' }),
          el('dd', { text: count ? formatZar(moduleCents) : '—' }),
        ]),
        el('div', { className: `pwc-line${this.#attachmentChosen ? '' : ' is-incomplete'}` }, [
          el('dt', { text: this.#attachmentChosen ? attachment.label : 'Attachment' }),
          el('dd', { text: this.#attachmentChosen ? (attachment.included ? 'Included' : formatZar(attachmentCents)) : 'Choose in step 3' }),
        ]),
        el('div', { className: `pwc-line${this.#assemblyLabel ? '' : ' is-incomplete'}` }, [
          el('dt', { text: this.#assemblyLabel || 'Assembly' }),
          el('dd', { text: this.#assemblyLabel ? 'No charge' : 'Choose in step 4' }),
        ])
      );
    }
    // Every module's colours, exactly as they will appear on the cart line and the order (summariseModule).
    const colours = this.#refs['summary-modules'];
    if (colours) {
      colours.hidden = count === 0;
      const list = colours.querySelector('ol');
      if (list) replaceChildren(list, ...this.#modules.map((module) => el('li', { text: schema.summariseModule(module, this.#catalogue) })));
    }
    // Never present R0 as the build's price: until the configuration is valid, show the prompt
    // (section setting); as soon as it is valid, show the live total. The figure follows every change
    // (letters, icons, options, attachment) immediately.
    if (this.#refs.total) {
      const valid = !this.#priceBlocker;
      this.#refs.total.textContent = valid ? formatZar(totalCents) : this.#refs.total.dataset.prompt || 'Build your price';
      this.#refs.total.classList.toggle('is-prompt', !valid);
    }
    const text = this.#refs['summary-text'];
    if (text) {
      text.textContent = count
        ? `${this.#sequenceText()} · ${count} ${count === 1 ? 'module' : 'modules'}${this.#attachmentChosen ? ` · ${attachment.label}` : ''}${this.#assemblyLabel ? ` · ${this.#assemblyLabel}` : ''}`
        : 'Type a name or add an icon to start your build.';
      text.classList.toggle('is-incomplete', !count);
    }
    this.#renderAtc();
  }

  #renderAtc() {
    const button = this.#refs.atc;
    const label = this.#refs['atc-text'];
    if (!button || !label) return;
    const blocker = this.#blocker;
    if (this.#busy) return;
    button.disabled = Boolean(blocker);
    label.textContent = blocker || `Add to cart — ${formatZar(this.#pricing.totalCents)}`;
    button.classList.toggle('is-ready', !blocker);
  }

  /* ---------------------------------------------------------------------------- add to cart */

  async #addToCart() {
    if (this.#busy || this.#blocker) return;
    const button = this.#refs.atc;
    const label = this.#refs['atc-text'];
    const error = this.#refs.error;
    const notice = this.#refs.notice;
    error.hidden = true;
    notice.hidden = true;

    let config;
    let items;
    let variant;
    try {
      variant = schema.variantFor(this.#config?.variants, this.#modules.length, this.#attachment);
      if (!variant) throw new Error('this size is not available');
      config = schema.createConfiguration({
        configId: newConfigId(schema.ID_PREFIX),
        modules: this.#modules,
        attachmentId: this.#attachment,
        assemblyId: this.#assembly,
      });
      const problems = schema.validateConfiguration(config, this.#catalogue);
      if (problems.length) throw new Error(problems[0]);
      items = buildCartItems(schema.toCartBuild(config, this.#catalogue, variant.variantId));
    } catch (failure) {
      this.#showError(
        failure instanceof RangeError
          ? 'This build is too long to save with an order. Please contact us and we will build it for you.'
          : `This build cannot be added: ${failure.message}`
      );
      return;
    }

    this.#busy = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('is-loading');
    label.textContent = 'Adding…';
    announce(this, 'Adding your Name Clicker to the cart.');

    const result = await addBuildToCart({
      items,
      configId: config.configId,
      expected: { configuredVariantId: variant.variantId, configuredCents: config.charges.totalCents, lines: [] },
      // Build-level analytics fact (one custom pixel event per build; see pw-config-cart publishBuildAdded)
      analytics: { schema: schema.SCHEMA_ID, productId: this.#config?.configured?.productId, variantId: variant.variantId, totalCents: config.charges.totalCents, currency: 'ZAR', name: this.#config?.configured?.title || 'Build Your Own Name Clicker' },
    });

    this.#busy = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('is-loading');
    if (!result.ok) {
      this.#showError(result.error);
      this.#renderAtc();
      return;
    }
    notice.hidden = false;
    announce(this, `Added to cart: ${this.#sequenceText()}, ${formatZar(config.charges.totalCents)}.`);
    label.textContent = 'Added to cart';
    setTimeout(() => this.#renderAtc(), 3000);
  }

  #showError(message) {
    const error = this.#refs.error;
    if (!error) return;
    error.hidden = false;
    error.textContent = message;
    announce(this, message);
  }
}

if (!customElements.get('pw-name-clicker-builder')) {
  customElements.define('pw-name-clicker-builder', PwNameClickerBuilder);
}
